import React, { createContext, useContext, useState, useEffect } from "react";

type Theme = "light" | "dark";
export type Template = string;

interface TemplateStyles {
    light: Record<string, string>;
    dark: Record<string, string>;
}

// Default template colors
const defaultTemplate: TemplateStyles  = {
    light: {
        "--background-color": "#ffffff",
        "--input-label": "rgb(24, 55, 79)",
        "--text-color": "#505053",
        "--text-dark": "rgb(36, 36, 36)",
        "--shadow-color": "rgba(0, 0, 0, 0.3)",
        "--box-shadow": "0px 4px 8px rgba(0, 0, 0, 0.3)",
        "--box-shadow-inset": "0px 2px 6px rgba(0, 0, 0, 0.2), inset 0px 4px 6px rgba(0, 0, 0, 0.15)",
        "--card-bg": "linear-gradient(135deg, #ffffff, #f8f8f8)",
        "--title": "#344054",
        "--title-font": "Montserrat arm",
        "--smart-title-color": "#344054",
        "--smart-title-font": "Montserrat arm",
        "--smart-subtitle-color": "#344054",
        "--smart-subtitle-font": "Montserrat arm",
        "--input-bg": "#e4e2e2",
        "--primary-button": "#ee163a",
        "--primary-hover": "rgb(170, 0, 43)",
        "--disable-button": "#c1c2c4",
        "--icon-color": "#344054",
        "--footer-bg": "rgba(250,250,250,0.75)"
    },
    dark: {
        "--background-color": "rgb(12, 12, 12)",
        "--input-label": "rgba(210, 236, 255, 0.4)",
        "--text-color": "#ffffff",
        "--text-dark": "rgba(183, 201, 214, 0.6)",
        "--shadow-color": "rgba(255, 255, 255, 0.2)",
        "--box-shadow": "0px 4px 15px rgba(255, 255, 255, 0.1), 0px 2px 8px rgba(255, 255, 255, 0.05)",
        "--box-shadow-inset": "0px 0px 6px rgba(255, 255, 255, 0.2), inset 0px 2px 0px rgba(255, 255, 255, 0.1)",
        "--card-bg": "linear-gradient(135deg, #000, #1f1f1f)",
        "--title": "#ffffff",
        "--title-font": "Montserrat arm",
        "--smart-title-color": "#ffffff",
        "--smart-title-font": "Montserrat arm",
        "--smart-subtitle-color": "#ffffff",
        "--smart-subtitle-font": "Montserrat arm",
        "--input-bg": "rgb(37, 37, 37)",
        "--primary-button": "#ee163a",
        "--primary-hover": "rgb(170, 0, 43)",
        "--disable-button": "rgb(123, 123, 124)",
        "--icon-color": "#ffffff",
        "--footer-bg": "rgba(12, 12, 12,0.75)"
    }
};

// Store for custom templates
const customTemplates: Record<string, TemplateStyles > = {};
    


interface ThemeContextProps {
    theme: Theme;
    template: Template;
    toggleTheme: () => void;
    setTemplate: (template: Template) => void;
    setCustomTemplate: (templateName: string, template: TemplateStyles ) => void;
    getTemplate: (templateName: string) => TemplateStyles  | null;
    getAllTemplates: () => Record<string, TemplateStyles >;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        return (localStorage.getItem("bytes_theme") as Theme) || "light";
    });

    const [template, setTemplate] = useState<Template>("default");

    useEffect(() => {
        localStorage.setItem("bytes_theme", theme);
    }, [theme]);

    useEffect(() => {
        // Get template colors - use default if template is "default" or not found
        let templateStyles: TemplateStyles ;
        
        if (template === "default") {
            templateStyles = defaultTemplate;
        } else if (customTemplates[template]) {
            templateStyles = customTemplates[template];
        } else {
            templateStyles = defaultTemplate;
        }

        const colors = templateStyles[theme];
        Object.entries(colors).forEach(([key, value]) => {
            document.documentElement.style.setProperty(key, value);
        });
    }, [theme, template]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === "light" ? "dark" : "light"));
    };

    const setCustomTemplate = (templateName: string, template: TemplateStyles) => {
        customTemplates[templateName] = template;
    };

    const getTemplate = (templateName: string): TemplateStyles | null => {
        if (templateName === "default") {
            return defaultTemplate;
        }
        return customTemplates[templateName] || null;
    };

    const getAllTemplates = (): Record<string, TemplateStyles> => {
        return {
            default: defaultTemplate,
            ...customTemplates
        };
    };

    return (
        <ThemeContext.Provider value={{ 
            theme, 
            template, 
            toggleTheme, 
            setTemplate, 
            setCustomTemplate, 
            getTemplate, 
            getAllTemplates 
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error("useTheme must be used within a ThemeProvider");
    return context;
};

// Utility functions for working with templates
export const createTemplate = (name: string, lightColors: Record<string, string>, darkColors: Record<string, string>): TemplateStyles => {
    return {
        light: lightColors,
        dark: darkColors
    };
};

// Example usage function
export const applyCustomTemplate = (templateName: string, lightColors: Record<string, string>, darkColors: Record<string, string>) => {
    const template = createTemplate(templateName, lightColors, darkColors);
    // This would be used in a component that has access to the theme context
    return template;
};
